/**
 * Light Features Component
 * Handles text animation and mobile carousel functionality
 */
if (!window.LightFeatures) {
  class LightFeatures {
    constructor(section) {
      this.section = section;
      this.carousel = section.querySelector('[data-carousel-track]');
      this.currentSlide = 0;
      this.totalSlides = 3;
      
      this.init();
    }
  
    init() {
      // Initialize text animation
      this.initTextAnimation();
      
      // Initialize mobile carousel for mobile and iPad (768px - 1024px)
      if (this.shouldShowMobileCarousel()) {
        this.initMobileCarousel();
      }
  
      // Handle resize
      window.addEventListener('resize', () => {
        if (this.shouldShowMobileCarousel()) {
          this.initMobileCarousel();
        }
      });
    }
    
    /**
     * Check if mobile carousel should be shown
     * Mobile: <= 900px, iPad: 768px - 1024px
     */
    shouldShowMobileCarousel() {
      const width = window.innerWidth;
      return width <= 900 || (width >= 768 && width <= 1024);
    }
  
    /**
     * Initialize text animation effect
     * Text appears gradually with blur-to-clear effect, from left to right, top to bottom
     */
    initTextAnimation() {
      const textElements = this.section.querySelectorAll('[data-text-animation]');
      
      textElements.forEach(element => {
        // Check if already initialized
        if (element.hasAttribute('data-animation-initialized')) {
          return;
        }
        
        // Split text into characters while preserving HTML structure
        const parts = element.querySelectorAll('.text-part');
        const allChars = [];
        
        parts.forEach(part => {
          // Use innerHTML to preserve HTML tags like text-bold
          const html = part.innerHTML;
          part.innerHTML = '';
          
          // Parse the HTML and process nodes
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          
          this.processNodes(tempDiv, part, allChars);
        });

        // Mark as initialized
        element.setAttribute('data-animation-initialized', 'true');

        // Create intersection observer for animation trigger
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.animateText(allChars);
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.3,
          rootMargin: '0px 0px -100px 0px'
        });

        observer.observe(element);
      });
    }

    /**
     * Process DOM nodes and split text into words/characters while preserving HTML structure
     * @param {Node} sourceNode - Source node to process
     * @param {Element} targetParent - Target parent element to append to
     * @param {Array} allChars - Array to collect all character spans
     */
    processNodes(sourceNode, targetParent, allChars) {
      sourceNode.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          // Split text by words (keep spaces as separators)
          const text = node.textContent;
          const words = text.split(/(\s+)/);
          
          words.forEach(word => {
            if (word === '') return;
            
            if (/^\s+$/.test(word)) {
              // It's whitespace - create space spans
              for (let i = 0; i < word.length; i++) {
                const spaceSpan = document.createElement('span');
                spaceSpan.classList.add('text-char');
                spaceSpan.innerHTML = '&nbsp;';
                targetParent.appendChild(spaceSpan);
                allChars.push(spaceSpan);
              }
            } else {
              // It's a word - wrap in a word container to prevent breaking
              const wordSpan = document.createElement('span');
              wordSpan.classList.add('text-word');
              
              for (let i = 0; i < word.length; i++) {
                const charSpan = document.createElement('span');
                charSpan.classList.add('text-char');
                charSpan.textContent = word[i];
                wordSpan.appendChild(charSpan);
                allChars.push(charSpan);
              }
              
              targetParent.appendChild(wordSpan);
            }
          });
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Preserve element structure (like text-bold spans)
          const newElement = document.createElement(node.tagName.toLowerCase());
          
          // Copy all attributes
          for (const attr of node.attributes) {
            newElement.setAttribute(attr.name, attr.value);
          }
          
          targetParent.appendChild(newElement);
          
          // Recursively process child nodes
          this.processNodes(node, newElement, allChars);
        }
      });
    }
  
    /**
     * Animate text characters with blur and fade effect using RAF
     * Creates a "wave" effect where multiple characters appear at once
     * @param {Array} chars - Array of character span elements
     */
    animateText(chars) {
      if (!chars || chars.length === 0) return;
  
      // Animation configuration - faster appearance with batch effect
      const batchSize = 5; // Number of characters to show at once (creates row-by-row feel)
      const batchDelay = 40; // ms between each batch (faster overall)
      const startDelay = 100; // shorter initial delay
      
      let startTime = null;
      let currentIndex = 0;

      /**
       * RAF animation loop - batch characters for wave effect
       * @param {number} timestamp - Current timestamp from RAF
       */
      const animate = (timestamp) => {
        // Initialize start time
        if (!startTime) {
          startTime = timestamp;
        }

        const elapsed = timestamp - startTime;

        // Wait for start delay
        if (elapsed < startDelay) {
          requestAnimationFrame(animate);
          return;
        }

        const animationTime = elapsed - startDelay;

        // Calculate which batch we should be at
        const targetBatch = Math.floor(animationTime / batchDelay);
        const targetIndex = Math.min(targetBatch * batchSize + batchSize - 1, chars.length - 1);

        // Show all characters up to targetIndex in current batch
        while (currentIndex <= targetIndex && currentIndex < chars.length) {
          chars[currentIndex].classList.add('visible');
          currentIndex++;
        }

        // Continue animation if there are more characters
        if (currentIndex < chars.length) {
          requestAnimationFrame(animate);
        }
      };

      // Start the animation
      requestAnimationFrame(animate);
    }
  
    /**
     * Initialize mobile carousel functionality
     */
    initMobileCarousel() {
      if (!this.carousel) return;

      this.cards = this.carousel.querySelectorAll('.mobile-card');
      this.prevBtn = this.section.querySelector('[data-carousel-prev]');
      this.nextBtn = this.section.querySelector('[data-carousel-next]');
      this.currentSlideEl = this.section.querySelector('[data-current-slide]');
      this.totalSlidesEl = this.section.querySelector('[data-total-slides]');
      this.isDragging = false;
      this.startPos = 0;
      
      if (!this.prevBtn || !this.nextBtn) return;
      if (this.cards.length === 0) return;

      this.totalSlides = this.cards.length;

      // Set total slides
      if (this.totalSlidesEl) {
        this.totalSlidesEl.textContent = this.totalSlides;
      }

      if (this.carouselInitialized) {
        this.updateCarousel();
        return;
      }
      this.carouselInitialized = true;

      // Previous button
      this.prevBtn.addEventListener('click', () => {
        this.goToPrevSlide();
      });

      // Next button
      this.nextBtn.addEventListener('click', () => {
        this.goToNextSlide();
      });

      // Touch/swipe support
      this.initTouchSupport();

      // Initial carousel update
      this.updateCarousel();
    }

    /**
     * Go to previous slide
     */
    goToPrevSlide() {
      if (this.currentSlide > 0) {
        this.currentSlide--;
        this.updateCarousel();
      }
    }

    /**
     * Go to next slide
     */
    goToNextSlide() {
      // For iPad, max slide is totalSlides - 2 (show 2 cards at a time)
      const maxSlide = this.isIPadViewport() ? Math.max(0, this.totalSlides - 2) : this.totalSlides - 1;
      if (this.currentSlide < maxSlide) {
        this.currentSlide++;
        this.updateCarousel();
      }
    }

    /**
     * Go to specific slide
     * @param {number} index - Slide index
     */
    goToSlide(index) {
      if (index >= 0 && index < this.totalSlides) {
        this.currentSlide = index;
        this.updateCarousel();
      }
    }

    /**
     * Check if current viewport is iPad (768px - 1024px)
     */
    isIPadViewport() {
      const width = window.innerWidth;
      return width >= 768 && width <= 1024;
    }
    
    /**
     * Update carousel position using transform
     */
    updateCarousel() {
      if (!this.carousel || !this.cards || this.cards.length === 0) return;

      const card = this.cards[this.currentSlide];
      const cardWidth = card.offsetWidth;
      const trackWidth = this.carousel.offsetWidth;
      const distance = trackWidth - cardWidth;
      
      const gap = 12;
      let translateX = 0;
      
      // For iPad, only allow one slide transition (show 2 cards at a time, swipe to see 3rd)
      if (this.isIPadViewport()) {
        // On iPad, we show 2 cards at a time, so max slide is 1 (to show card 3)
        const maxSlide = Math.max(0, this.totalSlides - 2);
        if (this.currentSlide > maxSlide) {
          this.currentSlide = maxSlide;
        }
        translateX = -(this.currentSlide * (cardWidth + gap));
      } else {
        // Calculate transform to show current card and part of next card
        if (this.currentSlide === this.totalSlides - 1) {
          // Last slide - center it or adjust as needed
          translateX = -(this.currentSlide * (cardWidth + gap)) + distance;
        } else {
          translateX = -(this.currentSlide * (cardWidth + gap));
        }
        
        // Don't offset on the first slide
        if (this.currentSlide === 0) {
          translateX = 0;
        }
      }
      
      this.carousel.style.transform = `translateX(${translateX}px)`;

      // Update counter (only if visible)
      if (this.currentSlideEl) {
        this.currentSlideEl.textContent = this.currentSlide + 1;
      }

      // Update button states (only if visible)
      if (this.prevBtn) {
        this.prevBtn.disabled = this.currentSlide === 0;
      }
      if (this.nextBtn) {
        // For iPad, max slide is totalSlides - 2
        const maxSlide = this.isIPadViewport() ? Math.max(0, this.totalSlides - 2) : this.totalSlides - 1;
        this.nextBtn.disabled = this.currentSlide >= maxSlide;
      }
    }

    /**
     * Initialize touch/swipe support for mobile
     */
    initTouchSupport() {
      if (!this.carousel) return;

      // Touch events - using Swiper-like angle detection
      this.carousel.addEventListener('touchstart', (e) => {
        this.isDragging = true;
        this.startPos = e.touches[0].clientX;
        this.startPosY = e.touches[0].clientY;
        this.touchStartTime = Date.now();
        this.isScrolling = undefined; // undefined = not determined yet
        this.isMoved = false;
        
        // Set touch-action via JS as fallback
        this.carousel.style.touchAction = 'pan-y';
      }, { passive: true });

      this.carousel.addEventListener('touchmove', (e) => {
        if (!this.isDragging) return;
        
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const diffX = currentX - this.startPos;
        const diffY = currentY - this.startPosY;
        
        // Determine scroll direction on first move (like Swiper does)
        if (typeof this.isScrolling === 'undefined') {
          // Use angle-based detection like Swiper
          // touchAngle of 45 degrees means if angle is less than 45°, it's horizontal
          const touchAngle = Math.atan2(Math.abs(diffY), Math.abs(diffX)) * 180 / Math.PI;
          this.isScrolling = touchAngle > 45; // true = vertical scrolling, false = horizontal swiping
        }
        
        // If horizontal swipe detected, prevent vertical scrolling
        if (!this.isScrolling) {
          if (e.cancelable) {
            e.preventDefault();
            e.stopPropagation();
          }
          this.isMoved = true;
          
          // Temporarily disable touch-action for smoother horizontal swipe
          this.carousel.style.touchAction = 'none';
        }
        
        // Add resistance at boundaries
        if (!this.isScrolling) {
          if ((this.currentSlide === 0 && diffX > 0) || 
              (this.currentSlide === this.totalSlides - 1 && diffX < 0)) {
            return;
          }
        }
      }, { passive: false }); // passive: false to allow preventDefault

      this.carousel.addEventListener('touchend', (e) => {
        if (!this.isDragging) return;
        
        const endPos = e.changedTouches[0].clientX;
        const diff = endPos - this.startPos;
        const threshold = 50; // Minimum swipe distance
        const timeDiff = Date.now() - this.touchStartTime;
        
        // Restore touch-action
        this.carousel.style.touchAction = 'pan-y';

        // Only change slide if it was a horizontal swipe (not scrolling)
        if (!this.isScrolling && this.isMoved && Math.abs(diff) > threshold) {
          // Fast swipe detection - lower threshold for quick swipes
          const isQuickSwipe = timeDiff < 300 && Math.abs(diff) > 20;
          
          if (isQuickSwipe || Math.abs(diff) > threshold) {
            if (diff > 0) {
              this.goToPrevSlide();
            } else {
              this.goToNextSlide();
            }
          }
        }

        this.isDragging = false;
        this.isScrolling = undefined;
        this.isMoved = false;
      });

      // Mouse events (for desktop dragging)
      this.carousel.addEventListener('mousedown', (e) => {
        this.isDragging = true;
        this.startPos = e.clientX;
        this.carousel.style.cursor = 'grabbing';
        e.preventDefault();
      });

      this.carousel.addEventListener('mousemove', (e) => {
        if (!this.isDragging) return;
        e.preventDefault();
      });

      this.carousel.addEventListener('mouseup', (e) => {
        if (!this.isDragging) return;
        
        const endPos = e.clientX;
        const diff = endPos - this.startPos;
        const threshold = 50;

        if (Math.abs(diff) > threshold) {
          if (diff > 0) {
            this.goToPrevSlide();
          } else {
            this.goToNextSlide();
          }
        }

        this.isDragging = false;
        this.carousel.style.cursor = 'grab';
      });

      this.carousel.addEventListener('mouseleave', () => {
        if (this.isDragging) {
          this.isDragging = false;
          this.carousel.style.cursor = 'grab';
        }
      });
    }
  }
  
  // Export to global scope
  window.LightFeatures = LightFeatures;
}

$(window).ready(function() {
  var tooSmall = false;
  var maxWidth = 991;

  if( $(window).width() < maxWidth ) {
    tooSmall = true;
  }

  function initScrollMagic() {
    const lightFeaturesController = new ScrollMagic.Controller();
    
    new ScrollMagic.Scene({
      triggerElement: ".main-feature-section",
      triggerHook: 0.9
    })
    .setClassToggle(".main-feature-section", "active")
    .addTo(lightFeaturesController);
    
    
    new ScrollMagic.Scene({
      triggerElement: ".features-grid",
      triggerHook: 0.8
    })
    .setClassToggle(".features-grid", "active")
    .addTo(lightFeaturesController);
    
  }

  if( !tooSmall ) {
    initScrollMagic();
  }
})